import { NetworkStatus } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import { Combobox } from '@base-ui/react/combobox';
import { faAngleDown, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import debounce from 'lodash/debounce';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import {
  ExerciseDetailsFragment,
  ExercisesDocument,
} from '../../../graphql/types';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import { BORDER_GRAY, HOVER, WHITE } from '../../../theme';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventSubCategories,
} from '../../../utils/trackingEvents';
import { Highlight } from '../../Highlight';
import LoadingPanel from '../../LoadingPanel';
import { ButtonSize } from '../../reusable/Button';
import {
  IconButtonCSS,
  IconButtonCSSProps,
} from '../../reusable/Button/IconButton';
import PaginationWaypoint from '../../reusable/PaginationWaypoint';
import Text, { TextColor, TextKind, textStyleFrom } from '../../reusable/Text';
import { TextLineClamp } from '../../reusable/TextLineClamp';
import { ExercisePreview } from './ExercisePreview';

const InputContainer = styled.div`
  width: 100%;
  display: inline-flex;
  border: 1px solid ${BORDER_GRAY};
  border-radius: 4px;
  align-items: center;
  position: relative;
  overflow: hidden;

`;

const Input = styled.input`
  border: none;
  padding: 8px;
  outline: none;
  flex: 1;
  padding-right: 48px;
  
  ${textStyleFrom(TextKind.BodyM, TextColor.Primary)};
`;

const StyledComboboxActions = styled.div`
  display: flex;
  align-items: center;

  position: absolute;
  right: 4px;
`;

const StyledComboboxButton = styled.button<IconButtonCSSProps>`
  ${IconButtonCSS}
`;

const Items = styled.div`
  width: var(--anchor-width);
  max-width: var(--available-width);
    
  display: flex;
  flex-direction: column;

  border-radius: 4px;
  border: 1px solid ${BORDER_GRAY};
  background: ${WHITE};
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.1);

  z-index: 1000;

  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;

`;

const Item = styled.div`
  margin: 4px;
  margin-bottom: 0;
  border-radius: 6px;
  list-style: none;
  cursor: pointer;
  background: ${WHITE};
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 8px;
  position: relative;


  &[data-highlighted] {
    background: ${HOVER};
  }
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  gap: 4;
  padding: 8px 12px 8px 24px;
`;

const StyledStatus = styled.div`
  border-radius: 6px;
  margin: 4px;
  margin-bottom: 0;
  padding: 12px 12px 8px 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${WHITE};
`;

interface ExerciseAutocompleteProps {
  value: ExerciseDetailsFragment | null;
  onChange: (value: ExerciseDetailsFragment) => void;
  trackingData?: TrackingEventData;
}

const ITEMS_PER_PAGE = 10;

export const ExerciseAutocomplete: React.FC<ExerciseAutocompleteProps> = ({
  value,
  onChange,
  trackingData,
}) => {
  const { track } = useTracking();

  const [searchValue, setSearchValue] = React.useState(
    value?.displayName || '',
  );

  // Re-sync the input text when the parent swaps the selected exercise (e.g.
  // a "replace" flow from MovementDetailModal) — keying on `id` so a same-id
  // re-render with an updated displayName doesn't clobber an in-progress
  // search the user is typing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-syncs label only on identity change
  React.useEffect(() => {
    setSearchValue(value?.displayName || '');
  }, [value?.id]);

  const [
    executeQuery,
    {
      data,
      loading,
      error,
      fetchMore,
      previousData,
      variables,
      networkStatus,
      called,
    },
  ] = useLazyQuery(ExercisesDocument, {
    notifyOnNetworkStatusChange: true,
  });

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
  // `notifyOnNetworkStatusChange` flips `loading` true during pagination too,
  // so the initial / new-search load is "loading but not fetching more".
  const isSearching = !called || (loading && !isFetchingMore);

  const query = React.useMemo(
    () =>
      debounce((searchQuery: string) => {
        executeQuery({
          variables: {
            input: {
              first: ITEMS_PER_PAGE,
            },
            filter: {
              name: searchQuery || undefined,
            },
          },
        });
      }, 300),
    [executeQuery],
  );

  // clean up the debounced query
  React.useEffect(() => () => query.cancel(), [query]);

  const hasNextPage = data?.exercises?.pageInfo?.hasNextPage || false;
  const endCursor = data?.exercises?.pageInfo?.endCursor;

  // Fetch more when waypoint is reached
  const fetchMoreExercises = useCallback(() => {
    if (!hasNextPage || loading || isFetchingMore || !endCursor) {
      return;
    }
    fetchMore({
      variables: {
        input: {
          first: ITEMS_PER_PAGE,
          after: endCursor,
        },
        filter: {
          name: searchValue || undefined,
        },
      },
    });
  }, [hasNextPage, loading, isFetchingMore, endCursor, fetchMore, searchValue]);

  // Always surface the currently-selected exercise first, then the search
  // results with any duplicate of the selected one removed.
  const items = React.useMemo(() => {
    const results = (data || previousData)?.exercises.edges.map(
      (edge) => edge.node,
    ) ?? [value];
    return [value, ...results.filter((exercise) => exercise?.id !== value?.id)];
  }, [data, previousData, value]);

  const handleOpenChange = (open: boolean) => {
    track(EventNames.DropDownMenuClick, {
      event_label: EventLabels.TrainingPlanExercise,
      event_category: EventCategories.TrainingPlans,
      event_subcategory: open
        ? EventSubCategories.Open
        : EventSubCategories.Close,
      ...trackingData,
    });
    const variablesHaveChanged = variables?.filter?.name !== searchValue;
    if (open && variablesHaveChanged) {
      query(searchValue);
    }
  };

  const handleValueChange = (nextSelectedValue: ExerciseDetailsFragment) => {
    track(EventNames.DropDownMenuItemClick, {
      event_label: EventLabels.TrainingPlanExercise,
      event_category: EventCategories.TrainingPlans,
      exercise_id: nextSelectedValue?.id,
      ...trackingData,
    });
    onChange(nextSelectedValue);
    setSearchValue('');
  };

  const showHighlight = searchValue !== value?.displayName;
  const searchHighlightQuery = showHighlight
    ? searchValue?.trim().split(/[ ,]+/)
    : [];

  return (
    <Combobox.Root
      data-testid="exercise-autocomplete"
      items={items}
      value={value}
      filter={null}
      isItemEqualToValue={(item, value) => item.id === value.id}
      itemToStringLabel={(exercise) =>
        exercise.displayName || exercise.name || ''
      }
      highlightItemOnHover
      onValueChange={(nextSelectedValue) =>
        nextSelectedValue && handleValueChange(nextSelectedValue)
      }
      inputValue={searchValue}
      onOpenChange={handleOpenChange}
      onInputValueChange={(nextSearchValue) => {
        setSearchValue(nextSearchValue);

        query(nextSearchValue);
      }}
    >
      <InputContainer>
        <Combobox.Input
          render={(props) => <Input {...props} />}
          placeholder="e.g. Bench Press"
        />
        <StyledComboboxActions>
          <Combobox.Trigger
            render={(props) => (
              <StyledComboboxButton $size={ButtonSize.Small} {...props} />
            )}
            aria-label="Open popup"
          >
            <FontAwesomeIcon fontSize={16} icon={faAngleDown} />
          </Combobox.Trigger>
        </StyledComboboxActions>
      </InputContainer>

      <Combobox.Portal>
        <Combobox.Positioner style={{ zIndex: 1000 }} sideOffset={4}>
          <Combobox.Popup
            render={(props) => <Items {...props} />}
            aria-busy={loading || undefined}
          >
            <Combobox.Status>
              {isSearching ? (
                <StyledStatus>
                  <LoadingPanel
                    testId="exercise-autocomplete-loading"
                    noBorder
                    height={12}
                  />
                  <Text kind={TextKind.BodyM} color={TextColor.Secondary}>
                    Loading exercises...
                  </Text>
                </StyledStatus>
              ) : error ? (
                <StyledStatus>
                  <Text kind={TextKind.BodyM} color={TextColor.Error}>
                    Error loading exercises
                  </Text>
                </StyledStatus>
              ) : (
                items.length <= 1 && (
                  <StyledStatus>
                    <Text kind={TextKind.BodyM} color={TextColor.Secondary}>
                      No exercises found
                    </Text>
                  </StyledStatus>
                )
              )}
            </Combobox.Status>
            <Combobox.List style={{ gap: 4 }}>
              {(exercise: ExerciseDetailsFragment, index: number) => (
                <React.Fragment key={exercise.id}>
                  <Combobox.Item
                    value={exercise}
                    render={(props, { highlighted }) => (
                      <Item {...props}>
                        <ItemContent>
                          <Combobox.ItemIndicator
                            style={{
                              position: 'absolute',
                              left: 4,
                            }}
                          >
                            <FontAwesomeIcon fontSize={14} icon={faCheck} />
                          </Combobox.ItemIndicator>
                          <TextLineClamp $lines={2}>
                            <Highlight
                              style={{
                                ...textStyleFrom(
                                  TextKind.BodyM,
                                  TextColor.Primary,
                                ),
                                lineHeight: '1.2',
                              }}
                              query={searchHighlightQuery}
                            >
                              {exercise.displayName}
                            </Highlight>
                          </TextLineClamp>
                          <Text
                            kind={TextKind.BodyS}
                            color={TextColor.Secondary}
                          >
                            {exercise.defaultUnit?.name}
                          </Text>
                        </ItemContent>
                        <ExercisePreview
                          exercise={exercise}
                          playing={highlighted}
                        />
                      </Item>
                    )}
                  />
                  {index === items.length - 1 &&
                    hasNextPage &&
                    !isFetchingMore &&
                    !error && (
                      <PaginationWaypoint callback={fetchMoreExercises} />
                    )}
                  {index === items.length - 1 && isFetchingMore && (
                    <StyledStatus>
                      <LoadingPanel
                        testId="exercise-autocomplete-loading-more"
                        noBorder
                        height={12}
                      />
                      <Text kind={TextKind.BodyM} color={TextColor.Secondary}>
                        Loading more exercises...
                      </Text>
                    </StyledStatus>
                  )}
                </React.Fragment>
              )}
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  );
};
